import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Unsubscribable } from 'rxjs';
import { of } from 'rxjs/observable/of';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';
import { getBatchActionsSelectedStudentIds } from '../../../../store';
import { collapseOutAni } from '../../../animations/collapse-out.animation';
import { expandInAni } from '../../../animations/expande-in.animation';
import { IColumnStuLvl, IGroupData, IRowData, TList } from '../../../models/list-models';
import { BatchEditService } from '../../../services/batch-edit-service/batch-edit-service';
import { CellDisplayService } from '../../../services/list-services/cell-display.service';
import { HeaderService, IDisplayedHeader } from '../../../services/list-services/header.service';
import { SortAndFilterService, TSortDirections } from '../../../services/list-services/sort-and-filter.service';
import { IUser } from '../../../typings/interfaces/user.interface';
import { IRowDataWithBatchActions } from '../fixed-table/fixed-table.component';

/* istanbul ignore next */
@Component({
  selector: 'infinite-table',
  template: require('./infinite-table.component.html'),
  styles: [require('./infinite-table.component.scss')],
  animations: [expandInAni(), collapseOutAni()],
})
export class InfiniteTableComponent implements OnInit, AfterViewInit {
  // REQUIRED bindings if used along with FixedToInfiniteViewComponent
  @Input() schoolId: string;
  @Input() shelterId: string;
  @Input() groupData$: Observable<IGroupData[]>;
  @Input() sortKey$: BehaviorSubject<string>;
  @Input() sortDirection$: BehaviorSubject<TSortDirections>;
  @Input() columns: IColumnStuLvl[];
  @Input() filterFormControl: FormControl;
  @Input() batchActionsMode$: Observable<boolean>;
  @Input() columnIndexMap: { [key: string]: number };
  @Input() listConfig: any;
  @Output() collapseOutDone = new EventEmitter<{
    groupData: IGroupData;
    sortKey: string;
    sortDirection: TSortDirections;
  }>();
  @Output() clickedRowData = new EventEmitter<IRowData[]>();
  @Output() batchActionData = new EventEmitter<any>();
  @Output() uiRowData = new EventEmitter<[IGroupData]>();

  // OPTIONAL bindins
  @Input() currentUser: IUser;
  @Output() onDynamicComponentClick = new EventEmitter<any>();
  @Output() sortEmitter = new EventEmitter<string>();

  // available after view init
  @ViewChild(CdkVirtualScrollViewport, { static: false }) scrollViewport: CdkVirtualScrollViewport;

  // methods
  checkShadow: Function = HeaderService.checkHeaderShadow;
  getHeaderOverflow: Function = HeaderService.getHeaderOverflow;
  sortRowData: Function = SortAndFilterService.sortTableRowData;
  filterRows: Function = SortAndFilterService.filterRows;
  getDisplayedHeaders: Function = HeaderService.getDisplayedHeaders;

  // all other props
  aniState: string = 'expanded';
  showShadow$: Observable<boolean>;
  headerOverflow: boolean = false;
  currentFilterFormValue: string;
  noDataMessage: string;
  displayedHeaders: IDisplayedHeader[];
  tableRows$ = new BehaviorSubject<IRowData[][]>(null);
  dataSource$ = new BehaviorSubject<IRowDataWithBatchActions[][] | IRowData[][]>([]);
  dataPipeline$: Observable<any>;
  sectionHeaderIsChecked: boolean = false;
  sectionHeaderIsInd: boolean;
  groupData: IGroupData;
  dataPipelineSub: Unsubscribable;
  isBatchActionActivated: boolean;

  constructor(private store: Store<any>, private batchEditService: BatchEditService) {}

  ngOnInit(): void {
    this.currentFilterFormValue = this.filterFormControl.value || '';
    const { listType, noDataMessage } = this.listConfig;
    this.noDataMessage = noDataMessage;

    // update table on changes to rowdata, sort, or filter(JJ)
    this.dataPipeline$ = combineLatest(
      this.groupData$,
      this.filterFormControl.valueChanges.pipe(startWith(this.currentFilterFormValue)),
      this.batchActionsMode$,
      this.sortKey$,
      this.sortDirection$,
      this.store.select(getBatchActionsSelectedStudentIds),
    ).pipe(
      tap(([groupData, filterTerm, isBatchActionActive, sortKey, sortDirection, batchEditIds]) => {
        const { rowData } = groupData;
        this.groupData = groupData;
        this.tableRows$.next(rowData);
        this.displayedHeaders = this.getDisplayedHeaders(this.columns, groupData);
        const filteredRowData = this.filterRows({ filterTerm, listType, rowData });
        const sortKeyIndex = this.columnIndexMap[sortKey];
        const sortedRowData = [...this.sortRowData(filteredRowData, sortKeyIndex, sortDirection, sortKey)];

        const formattedRowData = CellDisplayService.getFormattedRowData({
          listType,
          sortedRowData,
          columns: this.columns,
        });

        this.uiRowData.emit([{ ...this.groupData, ...{ rowData: sortedRowData } }]);

        // for keeping track of batchActionsMode$ without manual subscription across the component lifecycle
        this.isBatchActionActivated = isBatchActionActive;
        const rowDataWithOptionalBatchActions = isBatchActionActive
          ? this.getExistingBatchActionSelections(formattedRowData, batchEditIds)
          : formattedRowData;

        this.dataSource$.next(rowDataWithOptionalBatchActions);

        this.currentFilterFormValue = filterTerm;
      }),
    );
    this.dataPipelineSub = this.dataPipeline$.subscribe();
  }

  public updateSort(sortKey: string): void {
    this.sortEmitter.emit(sortKey);
  }

  ngAfterViewInit(): void {
    this.showShadow$ = this.scrollViewport.elementScrolled().pipe(
      debounceTime(10),
      switchMap(e => of(this.checkShadow((e.target as HTMLElement).scrollTop))),
    );
  }

  emitUiRowData(uiRowData: [IGroupData]): void {
    this.uiRowData.emit(uiRowData);
  }

  emitCollapseOutDone(): void {
    if (this.aniState === 'collapsed') {
      this.collapseOutDone.emit({
        groupData: this.groupData,
        sortKey: this.sortKey$.value,
        sortDirection: this.sortDirection$.value,
      });
    }
  }

  emitRowClick(rowData: IRowData[], dataColumn?): void {
    if (dataColumn && dataColumn.dynamic) return;

    if (!this.isBatchActionActivated) {
      this.clickedRowData.emit(rowData);
    } else {
      this.emitBatchActionRowId(rowData);
    }
  }

  emitDynamicComponent(data: any): void {
    this.onDynamicComponentClick.emit(data);
  }

  onClear(): void {
    this.filterFormControl.setValue('');
  }

  // pull all this out
  getExistingBatchActionSelections(rowData: IRowData[][], batchEditIds: string[]): IRowDataWithBatchActions[][] {
    const rowIdHash = this.batchEditService.getSelectedRowsHash(batchEditIds);
    const listType = this.listConfig.listType;
    const { mappedData, count } = this.batchEditService.getRowDataWithBatchActions(rowIdHash, rowData, listType);
    this.sectionHeaderIsChecked = rowData.length === count ? true : false;
    this.sectionHeaderIsInd = count && rowData.length > count ? true : false;
    return mappedData;
  }

  emitBatchActionRowId(data: IRowData[]): void {
    this.sectionHeaderIsChecked = false;
    const batchData = { data, updateAll: this.sectionHeaderIsChecked, level: 'ROW' };
    this.batchActionData.emit(batchData);
  }

  emitBatchActionSectionIds(): void {
    this.sectionHeaderIsChecked = this.sectionHeaderIsChecked ? false : true;
    const data = this.dataSource$.value;
    const batchData = { data, updateAll: this.sectionHeaderIsChecked, level: 'SECTION' };
    this.batchActionData.emit(batchData);
  }

  checkHeaderOverflow(className: string): void {
    this.headerOverflow = this.getHeaderOverflow(className);
  }

  resetHeaderOverflow(): void {
    this.headerOverflow = false;
  }

  ngOnDestroy(): void {
    this.dataPipelineSub.unsubscribe();
  }
}
